import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { LineDot } from "../../../../../../assets/icons/LineDot";
import {
  ChartTitle,
  StyledSubtitle,
  StyledTile,
} from "../../../../../../components/ChartCard/ChartCard.styles";
import { ChartCardLoading } from "../../../../../../components/ChartCard/ChartCardLoading";
import { ChartSpecificValues } from "../../../../../../components/ChartCard/ChartSpecificValues/ChartSpecificValues";
import { StyledLegend } from "../../../../../../components/CustomChartLegend/CustomChartLegend.styles";
import { ChartLegendContainer } from "../../../../../../components/ModalChart/CustomModalChartLegend/CustomModalChartLegend.style";
import { useFitbitSleep } from "../../../../../../hooks/queries/fitbit";
import { useUserPreferences } from "../../../../../../hooks/useUserPreferences";
import { formatHoursAndMinutes } from "../../../../../../utils/dateFormatter";
import {
  FitbitSleepChart,
  FitbitSleepChartRow,
} from "../../../MeasurementModal/components/FitbitSleepChart/FitbitSleepChart";
import {
  FitbitSleepCardContainer,
  GraphContainer,
  GraphLegend,
} from "./FitbitSleepChartCard.styles";

export type FitbitSleepChartCardProps = {
  patientId?: string;
  title: string;
  onClick: React.MouseEventHandler<HTMLDivElement>;
};

export function FitbitSleepChartCard({
  patientId,
  title,
  onClick,
}: FitbitSleepChartCardProps) {
  const { t } = useTranslation();

  const { getDateRangeFilter } = useUserPreferences();
  const dateRangeFilter = getDateRangeFilter();

  const { sleepData, isSleepFetching, isSleepRefetching } = useFitbitSleep(
    parseInt(patientId ?? "0", 10),
    dateRangeFilter.startDate.startOf("day"),
    dateRangeFilter.endDate.endOf("day")
  );

  const specificValues = useMemo(() => {
    if (!sleepData?.statistics) {
      return [
        { label: t("patientPage_card_average"), value: "-" },
        { label: t("patientPage_card_highest"), value: "-" },
      ];
    }

    const { statistics } = sleepData;

    return [
      {
        label: t("patientPage_card_average"),
        value: formatHoursAndMinutes(statistics.averageHoursAsleep),
      },
      {
        label: t("patientPage_card_highest"),
        value: formatHoursAndMinutes(statistics.highestHoursAsleep),
      },
    ];
  }, [sleepData?.statistics, t]);

  const chartData: FitbitSleepChartRow[] = useMemo(() => {
    if (!sleepData) return [];

    return sleepData.days.map((day) => {
      const [yearStr, monthStr, dayStr] = day.date.split("T")[0].split("-");
      const date = new Date(
        parseInt(yearStr, 10),
        parseInt(monthStr, 10) - 1,
        parseInt(dayStr, 10),
        12,
        0,
        0,
        0
      );
      const session = sleepData.sessions.find(
        (s) =>
          s.isMainSleep &&
          s.dateOfSleep.split("T")[0] === day.date.split("T")[0]
      );
      return {
        timeValue: date.getTime(),
        hoursAsleep: day.hoursAsleep,
        minutesAsleep: day.minutesAsleep,
        sessionId: session?.id,
        provider: session?.provider,
        stageSummary: session?.stageSummary ?? null,
      };
    });
  }, [sleepData]);

  const providerLabel = useMemo(() => {
    const provider = sleepData?.sessions.find((s) => s.isMainSleep)?.provider;
    if (provider === "GoogleHealth") return "Google Health";
    return provider ?? "Fitbit";
  }, [sleepData]);

  const isEmptyState = !sleepData || chartData.length === 0;

  return (
    <StyledTile onClick={onClick}>
      {isSleepFetching || isSleepRefetching ? (
        <ChartCardLoading />
      ) : (
        <>
          <ChartTitle>{title}</ChartTitle>
          {isEmptyState ? (
            <StyledSubtitle>{t("patientPage_card_noData")}</StyledSubtitle>
          ) : null}

          <FitbitSleepCardContainer isEmpty={chartData.length === 0}>
            <GraphContainer isEmpty={chartData.length === 0}>
              {chartData.length > 0 ? (
                <GraphLegend>
                  <ChartLegendContainer>
                    <StyledLegend key="sleep">
                      <LineDot fill="#54A6AA" />
                      {providerLabel}
                    </StyledLegend>
                  </ChartLegendContainer>
                </GraphLegend>
              ) : null}
              <FitbitSleepChart
                data={chartData}
                dateInterval={dateRangeFilter}
                height="90%"
                width="100%"
                onlyGraph
              />
            </GraphContainer>

            <ChartSpecificValues
              values={specificValues}
              isEmpty={chartData.length === 0}
            />
          </FitbitSleepCardContainer>
        </>
      )}
    </StyledTile>
  );
}
